import { ApolloQueryResult } from 'apollo-client';
import * as Cookie from 'js-cookie';
import { LOCATION_CHANGED, LocationChangedAction, replace } from 'redux-little-router';
import { Epic, ofType } from 'redux-observable';
import { concat, EMPTY, from, Observable, of } from 'rxjs';
import { catchError, first, mergeMap } from 'rxjs/operators';

import { Action } from '../actions';
import { requestClientFreelancerData } from '../actions/clientFreelancerData';
import {
    CHANGE_LOCATION_HARD,
    ChangeLocationHard,
    DELETE_COOKIE,
    DeleteCookie,
    FETCH_COMMON_DATA,
    FETCH_TIME_ZONE_GROUPS,
    FetchCommonData,
    HIDE_RECOMMENDATIONS_TOOLTIP,
    redirect,
    REDIRECT_TO_LOCATION,
    RedirectToLocationAction,
    REJECT_REQUEST_AUTHORIZATION_DATA,
    rejectRequestAuthorizationData,
    RELOAD_LOCATION,
    REQUEST_AUTHORIZATION_DATA,
    RequestAuthorizationDataAction,
    SET_COOKIE,
    SetCookie,
    showCommonDataFetchError,
    UPDATE_DOCUMENT_TITLE,
    updateAuthorizationData,
    updateCommonData,
    UpdateDocumentTitle,
    updateTimeZoneGroups
} from '../actions/common';
import {
    OnGridSourceParamsChanged,
    onGridSourceParamsChanged,
    SetGridDocumentTitle,
    setGridDocumentTitle,
    updateCalcTypeFilter
} from '../actions/grid-screen';
import { fetchHeaderInfo, setUnauthorizedHeaderData, updateHeaderAvailableCampaignTypes } from '../actions/header';
import { mapTimeZoneGroupsFromApiFormat } from '../mappers/campaignsEdit/campaignsEdit';
import { mapAuthorizationData } from '../mappers/common/common';
import { RoutesPath } from '../routes/index';
import { selectGridSourceParams } from '../selectors/gridSourceParams/gridSourceParams';
import { ApiMethodExecutingError } from '../service/Errors';
import { GridScreenLinkBuilder } from '../service/GridScreenLinkBuilder';
import { GridSourceHelper } from '../service/GridSourceHelper';
import { HeaderHelpers } from '../service/HeaderHelpers/HeaderHelpers';
import '../service/Rx';
import { Session } from '../service/Session/Session';
import { EpicDeps } from '../service/StoreFactory';
import { UrlHelpers } from '../service/UrlHelpers';
import { campaigns, clients, CommonData } from '../typings/common';
import { CampaignsCalcTypeFilter } from '../typings/domain';
import { TimeZoneGroupsQuery } from '../typings/graphql-grid';
import { GridScreenUrlSearchQueryParams, GridScreenUrlSearchRouteParams } from '../typings/grid';
import { State } from '../typings/state';

const rejectRequestAuthorizationDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(REJECT_REQUEST_AUTHORIZATION_DATA).mapTo(setUnauthorizedHeaderData());
};

const commonDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_COMMON_DATA),
        first(),
        mergeMap(
            (action: FetchCommonData): Observable<Action> =>
                from(deps.commonDataProvider.getData(store$.value.commonData.login)).pipe(
                    mergeMap((commonData: CommonData) => {
                        const result: Action[] = [
                            updateCommonData(commonData),
                            updateHeaderAvailableCampaignTypes(commonData.availableCampaignTypes)
                        ];

                        // Пока CPM-кампании под фичей обрабатываем кейс когда фича у пользователя может пропасть
                        // todo@DIRECT-97722: оторвать после выпиливания фичи
                        const {
                            router: { route },
                            grid: { campaignsCalcTypeFilter }
                        } = store$.value;
                        const {
                            features: { isCpmCampaignsEnabled }
                        } = commonData;

                        if (
                            campaignsCalcTypeFilter === CampaignsCalcTypeFilter.CPM &&
                            !isCpmCampaignsEnabled &&
                            [RoutesPath.grid, RoutesPath.shortGrid].includes(route)
                        ) {
                            // Если в фильтре в гридах выбрано CPM, при этом фичи нет - принудительно переводим в CPC
                            result.push(updateCalcTypeFilter(CampaignsCalcTypeFilter.CPC));
                        }

                        return result;
                    }),
                    catchError(
                        (e: ApiMethodExecutingError): Observable<Action> => {
                            const { route } = store$.value.router;

                            switch (route) {
                                case RoutesPath.grid:
                                case RoutesPath.shortGrid:
                                case RoutesPath.campaignsEdit:
                                case RoutesPath.groupsEdit:
                                case RoutesPath.bannersEdit:
                                case RoutesPath.internalBannersEdit:
                                    return of(showCommonDataFetchError(e, true));
                                default:
                                    return Observable.empty();
                            }
                        }
                    )
                )
        )
    );
};

const changeLocationHardDataEpic: Epic<Action, Action, State, EpicDeps> = action$ => {
    return action$.ofType(CHANGE_LOCATION_HARD).mergeMap((action: ChangeLocationHard) => {
        UrlHelpers.openUrl(action.url, action.target);

        return EMPTY;
    });
};

const redirectToLocationEpic: Epic<Action, Action, State, EpicDeps> = action$ => {
    return action$.ofType(REDIRECT_TO_LOCATION).map((action: RedirectToLocationAction) => replace(action.location, {}));
};

const requestAuthorizationData: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) =>
    action$.pipe(
        ofType(REQUEST_AUTHORIZATION_DATA),
        mergeMap(
            (action: RequestAuthorizationDataAction): Observable<Action> => {
                return from(deps.commonDataProvider.fetchAuthorizationData()).pipe(
                    mergeMap(
                        (response): Observable<Action> =>
                            concat(
                                of(updateAuthorizationData(mapAuthorizationData(response.data))),
                                of(requestClientFreelancerData()),
                                of(fetchHeaderInfo())
                            )
                    ),
                    catchError(
                        (): Observable<Action> =>
                            concat(of(rejectRequestAuthorizationData()), of(setUnauthorizedHeaderData()))
                    )
                );
            }
        )
    );

const updateDocumentTitleEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_DOCUMENT_TITLE).mergeMap((action: UpdateDocumentTitle) => {
        document.title = action.title;

        return EMPTY;
    });
};

const setCookieEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(SET_COOKIE).mergeMap((action: SetCookie) => {
        const { key, value } = action;

        if (value) {
            Cookie.set(key, value);
        }

        return EMPTY;
    });
};

const deleteCookieEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(DELETE_COOKIE).mergeMap((action: DeleteCookie) => {
        Cookie.remove(action.key);

        return EMPTY;
    });
};

const reloadLocationEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(RELOAD_LOCATION).mergeMap(() => {
        window.location.reload();

        return EMPTY;
    });
};

const hideRecommendationTooltipEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(HIDE_RECOMMENDATIONS_TOOLTIP),
        mergeMap(() => {
            HeaderHelpers.hideRecommendationsTooltip();
            return EMPTY;
        })
    );
};

const onGridSourceParamsChangedEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$
        .ofType(LOCATION_CHANGED)
        .filter(() => {
            const location = store$.value.router.route;

            return location === RoutesPath.grid || location === RoutesPath.shortGrid;
        })
        .mergeMap(
            (
                action: LocationChangedAction
            ): Observable<RedirectToLocationAction | OnGridSourceParamsChanged | SetGridDocumentTitle> => {
                const { query = {}, params = {}, pathname } = action.payload;
                const { login } = store$.value.commonData;
                const sourceParams = selectGridSourceParams(store$.value);
                const {
                    campaignsCalcTypeFilter: campaignsCalcTypeFilterLocal
                } = Session.getValueFromCurrentLocalSession(login);
                const { campaignsIds, campaignsCalcTypeFilter: campaignsCalcTypeFilterRoute, view } = sourceParams;

                const calcTypeByView = {
                    usersProfile: [CampaignsCalcTypeFilter.CPM, CampaignsCalcTypeFilter.CPC],
                    retargeting: [CampaignsCalcTypeFilter.CPC]
                };
                const campaignsCalcTypeFilter = campaignsCalcTypeFilterLocal || campaignsCalcTypeFilterRoute;

                if (
                    (view in calcTypeByView &&
                        (!campaignsCalcTypeFilter || !calcTypeByView[view].includes(campaignsCalcTypeFilter))) ||
                    (params[GridScreenUrlSearchRouteParams.view] !== campaigns &&
                        (query[GridScreenUrlSearchQueryParams.campaignsIds] === undefined &&
                            (campaignsIds === undefined || campaignsIds.length === 0))) ||
                    !GridSourceHelper.isViewValid(params[GridScreenUrlSearchRouteParams.view])
                ) {
                    const defaultGridUrl = GridScreenLinkBuilder.getContextTypeUrl(
                        clients,
                        campaigns,
                        {
                            login
                        },
                        pathname
                    );
                    return Observable.of(redirect(defaultGridUrl));
                }

                return Observable.concat([
                    onGridSourceParamsChanged(sourceParams, action.payload.query || {}, action.payload.params || {}),
                    setGridDocumentTitle()
                ]);
            }
        );
};

const fetchTimeZoneGroupsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_TIME_ZONE_GROUPS),
        mergeMap(
            (): Observable<Action> => {
                const state = store$.value;
                const { login } = state.commonData;

                return from(deps.campaignsDataProvider.getTimeZoneGroups(login)).pipe(
                    mergeMap((timeZoneGroupsQuery: ApolloQueryResult<TimeZoneGroupsQuery>) => {
                        const timeZoneGroupsData = mapTimeZoneGroupsFromApiFormat(timeZoneGroupsQuery.data);

                        return of(updateTimeZoneGroups(timeZoneGroupsData));
                    })
                );
            }
        )
    );
};

export {
    commonDataEpic,
    requestAuthorizationData,
    updateDocumentTitleEpic,
    changeLocationHardDataEpic,
    setCookieEpic,
    reloadLocationEpic,
    deleteCookieEpic,
    redirectToLocationEpic,
    hideRecommendationTooltipEpic,
    rejectRequestAuthorizationDataEpic,
    onGridSourceParamsChangedEpic,
    fetchTimeZoneGroupsEpic
};
